// config
import { List, Map } from "immutable";
// dependencies
import moment from "moment";
import { createSelector } from "reselect";

import Config from "./Config";

// selectors
import { getPrimaryFields } from "../../../selectors/fields";

export const getUI = (state) => state.page.get(Config.pageName, Map());
export const getFormInputs = (state) => getUI(state).get("inputs", Map());
export const getCurrentPage = (state) => getUI(state).get("currentPage", 0);

export const getSelectionInputs = createSelector([getPrimaryFields, getUI], (primaryFields, ui) =>
	ui.get("columns", List()).concat(primaryFields)
);

export const getSelectionObjectID = (state) => state.navigation.getIn(["page", "query", "selectionObjectID"], null);

export const getHasUnsavedChanges = createSelector([getFormInputs, getSelectionObjectID], (inputs, selectionObjectID) =>
	Boolean(
		inputs.get("workflowTemplate") || inputs.get("workflowName") || inputs.get("startDate") || selectionObjectID
	)
);

/**
 * gets the todos from the UI. It mutates the List in a few ways:
 *  - replace any actor that has a value of `other employee` with a blank
 *    string.
 *  - replace any days less than `0` with a blank string
 */
export const getTodos = createSelector([getUI], (ui) => {
	const todos = ui.getIn(["workflowTodos"], List()).withMutations((mutable) => {
		mutable.forEach((todo, ii) => {
			// replace any negative `days to complete` with a blank string
			if (todo.getIn(["daysToComplete"], 0) < 0) {
				mutable.setIn([ii, "daysToComplete"], "");
			}
		});
	});

	return todos;
});

/** Determines if any todos in the ui are still in a bad state with a deactivated actor assigned **/
export const getDeactivatedActors = createSelector([getTodos], (todos) => {
	const nonDocumentSigningTodos = todos.filter((todo) => todo.get("documentSigning", null) === null);
	const hasInactiveActor = nonDocumentSigningTodos.some((todo) => {
		const actor = JSON.parse(JSON.stringify(todo.get("actor")));
		return actor?.active === false;
	});
	return hasInactiveActor;
});

/**
 * Determines if any of the actor inputs (todo or signature step) have a value
 * of 'manager'. Returns a boolean.
 */
export const getHasManagerAssigned = createSelector([getTodos], (todos) => {
	let hasManager = false;

	for (let ii = 0; ii < todos.size; ii += 1) {
		if (todos.getIn([ii, "actor", "id"]) === "manager") {
			hasManager = true;
			break;
		}

		const signingStepsCount = todos.getIn([ii, "documentSigning", "steps"], List()).size;

		for (let jj = 0; jj < signingStepsCount; jj += 1) {
			if (todos.getIn([ii, "documentSigning", "steps", jj, "signer", "id"]) === "manager") {
				hasManager = true;
				break;
			}
		}
	}

	return hasManager;
});

/**
 * Checks that all inputs have valid values
 */
export const getAllInputsAreValid = createSelector([getTodos], (todos) => {
	let hasInvalidInput = false;

	for (let ii = 0; ii < todos.size; ii += 1) {
		const hasAttachedDocument = Map.isMap(todos.getIn([ii, "documentSigning"]));

		if (todos.getIn([ii, "actor", "id"], "") === "" && !hasAttachedDocument) {
			hasInvalidInput = true;
			break;
		}

		if (todos.getIn([ii, "label"]) === "") {
			hasInvalidInput = true;
			break;
		}

		if (todos.getIn([ii, "daysToComplete"]) === "") {
			hasInvalidInput = true;
			break;
		}

		const signingStepsCount = todos.getIn([ii, "documentSigning", "steps"], List()).size;

		for (let jj = 0; jj < signingStepsCount; jj += 1) {
			if (todos.getIn([ii, "documentSigning", "steps", jj, "signer"], "") === "") {
				hasInvalidInput = true;
				break;
			}
		}
	}

	return !hasInvalidInput;
});

// get an error for todo labels
export const getTodoLabelsError = createSelector([getTodos], (todos) => {
	let errorFound = null;

	for (let ii = 0; ii < todos.size; ii += 1) {
		if (todos.getIn([ii, "label"]).length > 250) {
			errorFound = "To-do label can't exceed 250 characters.";
			break;
		}
	}

	return errorFound;
});

export const getDocumentPicker = (state) => getUI(state).getIn(["documentPicker"], null);
export const getViewDocumentModal = (state) => getUI(state).getIn(["viewDocumentModal"], null);

export const getManagerFallbackValue = (state) => getUI(state).get("managerFallbackUser", "");

/**
 * Gets a todo by its ID
 * @param {object} state - application state
 * @param {string} todoID - ID of the todo
 */
export const getTodoObject = (state, todoID) => getTodos(state).find((todo) => todo.get("id") === todoID);

/**
 * Massages the data for sending to the server
 * @param {object} state - application state
 */
export const getWorkflowData = (state) => {
	const formInputs = getFormInputs(state);

	const workflowTodos = getTodos(state).withMutations((mutable) => {
		mutable.forEach((todo, ii) => {
			// set the actor id
			mutable.setIn([ii, "actor"], todo.getIn(["actor", "id"]));

			// set document information
			if (todo.get("documentSigning")) {
				mutable.setIn([ii, "signatureRequestTemplateID"], todo.getIn(["documentSigning", "id"]));
				mutable.setIn(
					[ii, "signers"],
					todo.getIn(["documentSigning", "steps"]).map((step) => {
						// if signer is "other", set to empty string
						if (step.getIn(["signer", "id"]) === "other") {
							return "";
						}
						return step.getIn(["signer", "id"], null);
					})
				);
				mutable.deleteIn([ii, "actor"]);
			}

			mutable.deleteIn([ii, "documentSigning"]);
			mutable.deleteIn([ii, "id"]);
		});
	});

	return {
		name: formInputs.get("workflowName", null),
		templateID: formInputs.getIn(["workflowTemplate", "id"], null),
		startDate: moment.isMoment(formInputs.get("startDate")) ? formInputs.get("startDate").toISOString() : null,
		userSelectionID: getSelectionObjectID(state),
		todos: workflowTodos.toJS(),
		defaultManagerID: getUI(state).getIn(["managerFallbackUser", "id"], null),
	};
};

/** Gets the id of the last fetched workflow template */
export const getLastFetchedWorkflow = (state) => getUI(state).get("lastFetchedWorkflowTemplate", null);
